import axios from 'axios';
import { clearActiveCompany, getActiveCompanyId } from '../../helpers/activeCompany';

// A API recusa parâmetro repetido: a ordenação padrão só entra quando a query não traz
// a sua (várias colunas vão separadas por vírgula: order=a:DESC,b:ASC).
function withOrder(query = '', order) {
    return /(^|[?&])order=/.test(query) ? query : `${query}&order=${order}`;
}

export default class Axios {
    constructor() {
        this.axios = axios.create({
            baseURL: process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3044/v1/',
        });

        this.axios.interceptors.request.use((config) => {
            const token = localStorage.getItem('idToken_ow');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            const companyId = getActiveCompanyId();
            if (companyId) {
                config.headers['X-Company-Id'] = companyId;
            }
            return config;
        });

        this.axios.interceptors.response.use(
            (response) => {
                localStorage.setItem('last_page', window.location.pathname);
                return response;
            },
            (error) => {
                if (error.response) {
                    const code = error.response.data?.code;
                    const path = window.location.pathname;

                    // Empresa ativa inválida (sem vínculo) ou não escolhida por quem tem
                    // mais de uma: escolher de novo, sem deslogar, e voltar para esta tela.
                    if (code === 'COMPANY_FORBIDDEN' || code === 'COMPANY_REQUIRED') {
                        clearActiveCompany();
                        if (!path.startsWith('/auth/')) {
                            const returnTo = encodeURIComponent(path + window.location.search);
                            window.location.href = `/auth/select-company?returnTo=${returnTo}`;
                        }
                        return Promise.reject(error);
                    }

                    // Conta do Auth0 sem cadastro no OneWork. A tela de convite trata o
                    // próprio caso (é por ela que essa conta vira usuário).
                    if (code === 'USER_NOT_REGISTERED') {
                        if (!path.startsWith('/auth/') && !path.startsWith('/register/')) {
                            window.location.href = '/auth/conta-nao-cadastrada';
                        }
                        return Promise.reject(error);
                    }

                    // Se token expirado (403) ou não autorizado (401)
                    if (error.response.status === 403 || error.response.status === 401) {
                        const errorData = error.response.data;
                        
                        // Verifica se é erro de token expirado/inválido (o campo "error" do auth middleware vem como string)
                        const errorField = typeof errorData?.error === 'string' ? errorData.error : '';
                        if (errorField.includes('Token') || errorField.includes('expirado')) {
                            console.error('Token expirado, limpando localStorage e redirecionando...');
                            localStorage.removeItem('idToken_ow');
                            localStorage.removeItem('uid_ow');
                            localStorage.removeItem('user_ow');
                            
                            // Evita loop se já estiver na página de login
                            if (!window.location.pathname.includes('/auth/')) {
                                window.location.href = '/auth/entrar';
                            }
                            return Promise.reject(error);
                        }
                    }
                    
                    console.log(error.response);
                    return Promise.reject(error);
                }
                return Promise.reject(error);
            },
        );
    }

    async getServerTime() {
        return new Date();
    }

    async createUser(userData) {
        try {
            const res = this.axios.post(`auth/sign-up`, userData);
            return res;
        } catch (err) {
            console.log(err);
            return false;
        }
    }
    async addUser(userData) {
        try {
            const { data } = this.axios.post(`auth/add-user`, userData);
            return data;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async logoutUser() {
        try {
            const { data } = this.axios.get(`auth/log-out`);
            return data;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async loginUser(email, pass) {
        try {
            const res = await this.axios.post(`auth/entrar`, { email, pass });
            return res.data;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async me(query) {
        try {
            const res = await this.axios.get(`auth/me?${query}`);
            return res;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    // Métodos CRUD: deixam o erro do Axios propagar (com .response.data) para o BaseService,
    // que extrai a mensagem do backend no sendError. Antes esses métodos engoliam o erro e
    // retornavam err.toString() como string, sumindo com a mensagem real.

    async create(path, sendData) {
        return await this.axios.post(`${path}/`, sendData);
    }

    async destroy(path, id) {
        const { data } = await this.axios.delete(`${path}/${id}`);
        return data;
    }

    async restroy(path, id) {
        const { data } = await this.axios.patch(`${path}/${id}`);
        return data;
    }

    async update(path, sendData, id) {
        const { data } = await this.axios.put(`${path}/${id}`, sendData);
        return data;
    }

    async get(path, id) {
        const { data } = await this.axios.get(`${path}/${id}`);
        return data;
    }

    async list(path, query, order = 'id:desc') {
        const { data } = await this.axios.get(`${path}?${withOrder(query, order)}`);
        return data;
    }

    async count(path, query) {
        const { data } = await this.axios.get(`${path}/count?${query}`);
        return data;
    }

    async listAll(path, query, order = 'id:desc') {
        try {
            const { data } = await this.axios.get(`${path}/all?${withOrder(query, order)}`);
            return data;
        } catch (err) {
            console.log(err);
            return err.toString();
        }
    }
}
